import axios, { type AxiosProgressEvent } from 'axios'

export interface CurrentUser {
  id: string
  email: string
  username: string
  member_status: string
  member_expire_time: string | null
}

export interface AuthResponse {
  access_token: string
  token_type: string
  user: CurrentUser
}

export interface RiskItem {
  id: string
  title: string
  excerpt: string
  risk_level: 'high' | 'medium' | 'low' | null
  suggestion: string
  source?: 'llm' | 'rule'
  match_type?: 'matched' | 'missing' | 'unknown'
}

export interface ReviewResponse {
  exists?: boolean
  file_id: string
  file_name: string
  parse_status: string
  risk_count: number
  basic_advice: string
  risk_list: RiskItem[]
  contract_type: string
  raw_text: string
  unlocked: boolean
}

export interface UploadResponse {
  code: number
  message: string
  file_id: string
  progress: number
}

export interface ParseResponse {
  code: number
  file_id?: string
  file_name?: string
  message: string
  content: string
  parse_status: string
  parse_stage: string
  progress: number
  error: string
  contract_type?: string
}

export interface ReportAccessResponse {
  file_id: string
  unlocked: boolean
  reason: string
}

export interface OrderResponse {
  code: number
  pay_url: string
  order_id: string
  pay_status: string
  payment_provider?: string
}

export interface OrderStatusResponse {
  code?: number
  order_id: string
  pay_status: string
  payment_provider?: string
  amount: number
  order_type: string
  member_type: string | null
  pay_time: string | null
  expire_time: string
  pay_url?: string
  file_id?: string | null
  remaining_seconds?: number
  covered_by_membership?: boolean
}

export interface UserDashboardResponse {
  member_status: string
  member_expire_time: string | null
  recent_orders: OrderStatusResponse[]
  recent_files: Array<{
    file_id: string
    file_name: string
    parse_status: string
    unlocked: boolean
    review_time: string | null
  }>
}

export interface ExportResponse {
  blob: Blob
  fileName: string
}

const TOKEN_KEY = 'contract-risk-token'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? 'http://127.0.0.1:8000',
  timeout: 30000,
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export function saveAccessToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token)
}

export function clearAccessToken() {
  localStorage.removeItem(TOKEN_KEY)
}

export function getAccessToken() {
  return localStorage.getItem(TOKEN_KEY)
}

export async function registerUser(email: string, username: string, password: string) {
  const { data } = await api.post<AuthResponse>('/api/auth/register', {
    email,
    username,
    password,
  })
  return data
}

export async function loginUser(email: string, password: string) {
  const { data } = await api.post<AuthResponse>('/api/auth/login', {
    email,
    password,
  })
  return data
}

export async function fetchCurrentUser() {
  const { data } = await api.get<CurrentUser>('/api/auth/me')
  return data
}

export async function uploadContract(file: File, onProgress?: (percent: number) => void) {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await api.post<UploadResponse>('/api/file/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
      if (onProgress && progressEvent.total) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onProgress(percentCompleted)
      }
    },
  })
  return data
}

export async function parseContract(fileId: string) {
  const { data } = await api.get<ParseResponse>('/api/file/parse', {
    params: {
      file_id: fileId,
    },
  })
  return data
}

export async function startParseContract(fileId: string) {
  const { data } = await api.post<ParseResponse>('/api/file/parse/start', {
    file_id: fileId,
  })
  return data
}

export async function checkRisk(fileId: string, contractType?: string) {
  const { data } = await api.post<ReviewResponse>('/api/risk/check', {
    file_id: fileId,
    contract_type: contractType,
  })
  return data
}

export async function fetchReportAccess(fileId: string) {
  const { data } = await api.get<ReportAccessResponse>('/api/report/access', {
    params: {
      file_id: fileId,
    },
  })
  return data
}

export async function fetchReportDetail(fileId: string, contractType?: string) {
  const { data } = await api.get<ReviewResponse>('/api/report/detail', {
    params: {
      file_id: fileId,
      contract_type: contractType,
    },
  })
  return data
}

export async function createSingleOrder(fileId: string) {
  const { data } = await api.post<OrderResponse>('/api/pay/single', {
    file_id: fileId,
  })
  return data
}

export async function createMemberOrder(memberType: string) {
  const { data } = await api.post<OrderResponse>('/api/pay/member', {
    member_type: memberType,
  })
  return data
}

export async function confirmOrder(orderId: string) {
  const { data } = await api.post(`/api/pay/${orderId}/confirm`)
  return data
}

export async function fetchOrderStatus(orderId: string) {
  const { data } = await api.get<OrderStatusResponse>(`/api/pay/${orderId}`)
  return data
}

export async function cancelOrder(orderId: string) {
  const { data } = await api.post<OrderStatusResponse>(`/api/pay/${orderId}/cancel`)
  return data
}

export async function exportReport(fileId: string) {
  const response = await api.get<Blob>('/api/report/export', {
    params: {
      file_id: fileId,
    },
    responseType: 'blob',
  })

  const disposition = String(response.headers['content-disposition'] || '')
  const matchedFileName = disposition.match(/filename="?([^";]+)"?/i)?.[1]

  return {
    blob: response.data,
    fileName: matchedFileName ? decodeURIComponent(matchedFileName) : `report-${fileId}.pdf`,
  }
}

export async function fetchUserDashboard() {
  const { data } = await api.get<UserDashboardResponse>('/api/user/dashboard')
  return data
}

export async function submitFeedback(content: string) {
  const { data } = await api.post<{ code: number; message: string }>('/api/feedback', { content })
  return data
}